import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import type { FC, PropsWithChildren } from 'react';
import { type SxProps, type Theme } from '@mui/material/styles';
import { useThrottledNumberTuner } from './useThrottledNumberTuner';
import styled from 'styled-components';
import { FormControl } from '@mui/material';
import { ResetButtonWithConfirmation } from './ResetButtonWithConfirmation';

interface SliderTuneProps {
  descriptor: string;
  units: string;
  throttleMs: number;
  label?: string;
  subUnits?: { value: string; ratio: number };
  sliderProps?: {
    sx?: SxProps<Theme>;
    showValueAlways?: boolean;
  };
  locatableDescriptors?: Array<string>;
  hasReset?: boolean;
}

export function valuetext(units: string): (value: number) => string {
  return (value: number): string => `${value} ${units}`;
}

export const SliderTune: FC<PropsWithChildren<SliderTuneProps>> = ({ sliderProps, ...props }: SliderTuneProps) => {
  const { value, range, tuner, finalizer, undoValueSaver } = useThrottledNumberTuner(
    props.descriptor,
    props.throttleMs,
    props.locatableDescriptors
  );

  return (
    <Box>
      {props.label && (
        <FormControl fullWidth>
          <ControlContainer>
            <Typography id="input-slider" gutterBottom>
              {props.label}: {value.toFixed(0)} {props.units}
              {props.subUnits && (
                <span>
                  &nbsp; ({(value / props.subUnits.ratio).toFixed(1)} {props.subUnits.value})
                </span>
              )}
            </Typography>
            {props.hasReset && <ResetButtonWithConfirmation label={props.label} descriptors={[props.descriptor]} />}
          </ControlContainer>
        </FormControl>
      )}
      <StyledSlider
        sx={{
          ...(sliderProps?.sx ? sliderProps?.sx : {})
        }}
        aria-label={props.label}
        value={Math.round(value)}
        getAriaValueText={valuetext(props.units)}
        valueLabelFormat={valuetext(props.units)}
        valueLabelDisplay={sliderProps?.showValueAlways ? 'on' : 'auto'}
        step={1}
        data-testid={props.descriptor + 'SliderTuneBtn'}
        min={range[0]}
        max={range[1]}
        onMouseDown={(): void => undoValueSaver.saveUndoValue()}
        onChange={(_, value): void => tuner.doTune(value as number)}
        onChangeCommitted={(_, value): void => finalizer.doTune(value as number)}
        aria-labelledby="input-slider"
      />
    </Box>
  );
};

const ControlContainer = styled.div`
  position: relative;
  margin: 0.6em 0.6em 0 0;
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const StyledSlider = styled(Slider)``;
